import React, { useCallback, useEffect, useState } from "react";
import { Navigate, NavLink, Link, useNavigate } from "react-router-dom";
import { Loader2, Users, MessageSquare, Inbox, LogOut, Leaf, Home, Trash2, ExternalLink } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { api, formatApiErrorDetail } from "@/lib/api";

const TABS = [
  { key: "farmers", label: "Farmer Registrations", endpoint: "/admin/farmers", icon: Users },
  { key: "enquiries", label: "Product Enquiries", endpoint: "/admin/enquiries", icon: Inbox },
  { key: "contact", label: "Contact Messages", endpoint: "/admin/contact", icon: MessageSquare },
];

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <Card className="p-6 rounded-2xl border-border flex items-center gap-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${accent}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <div className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>{value}</div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      </div>
    </Card>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("farmers");
  const [data, setData] = useState({ farmers: [], enquiries: [], contact: [] });
  const [stats, setStats] = useState({ farmers: 0, enquiries: 0, messages: 0 });
  const [loadingData, setLoadingData] = useState(false);

  // `api`, `toast` and `formatApiErrorDetail` are stable module-level imports;
  // setState setters are stable per React contract. No dependencies needed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchAll = useCallback(async () => {
    setLoadingData(true);
    try {
      const [farmers, enquiries, contact, s] = await Promise.all([
        api.get("/admin/farmers"),
        api.get("/admin/enquiries"),
        api.get("/admin/contact"),
        api.get("/admin/stats"),
      ]);
      setData({ farmers: farmers.data, enquiries: enquiries.data, contact: contact.data });
      setStats(s.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load data");
    } finally {
      setLoadingData(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchAll();
    }
  }, [user, fetchAll]);

  const deleteEndpoint = (kind, id) => {
    if (kind === "farmers") return `/admin/farmers/${id}`;
    if (kind === "enquiries") return `/admin/enquiries/${id}`;
    return `/admin/contact/${id}`;
  };

  const onDelete = async (kind, id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(deleteEndpoint(kind, id));
      toast.success("Deleted");
      fetchAll();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out");
    navigate("/admin/login", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen flex bg-secondary/30" data-testid="admin-dashboard-page">
      {/* Sidebar */}
      <aside className="w-64 bg-primary text-white hidden md:flex flex-col p-6 sticky top-0 h-screen">
        <Link to="/" className="flex items-center gap-3 mb-10">
          <img src="/logo.png" alt="Indapur FPC" className="w-11 h-11 object-contain bg-white/10 rounded-full p-0.5" />
          <div>
            <div className="font-bold leading-tight" style={{ fontFamily: "Outfit" }}>Indapur FPC</div>
            <div className="text-[10px] uppercase tracking-widest text-white/60">Admin Panel</div>
          </div>
        </Link>

        <nav className="space-y-1 flex-1">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${tab === key ? "bg-white text-primary font-semibold" : "hover:bg-white/10"}`}
              data-testid={`sidebar-${key}`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </nav>

        <div className="mt-6 space-y-2">
          <Link
            to="/"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm hover:bg-white/10 transition-colors"
            data-testid="sidebar-view-site"
          >
            <Home className="w-4 h-4" /> View Site
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm hover:bg-white/10 transition-colors"
            data-testid="sidebar-logout"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 p-4 md:p-8">
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center justify-between mb-6">
          <div className="font-bold" style={{ fontFamily: "Outfit" }}>Admin Panel</div>
          <Button onClick={handleLogout} variant="outline" size="sm" className="rounded-full" data-testid="mobile-logout-btn">
            <LogOut className="w-4 h-4 mr-1.5" /> Logout
          </Button>
        </div>

        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold mb-1" style={{ fontFamily: "Outfit" }}>Welcome, {user.name}</h1>
          <p className="text-sm text-muted-foreground">Manage farmer submissions, enquiries and messages.</p>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <StatCard icon={Users} label="Farmers Registered" value={stats.farmers} accent="bg-primary text-white" />
          <StatCard icon={Inbox} label="Product Enquiries" value={stats.enquiries} accent="bg-accent text-white" />
          <StatCard icon={MessageSquare} label="Contact Messages" value={stats.messages} accent="bg-secondary text-primary" />
        </div>

        {/* Mobile tabs */}
        <div className="md:hidden mb-4 flex gap-2 overflow-x-auto">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs ${tab === key ? "bg-primary text-white" : "bg-white border border-border"}`}
              data-testid={`mobile-tab-${key}`}
            >
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>

        <Card className="rounded-2xl border-border overflow-hidden" data-testid={`table-${tab}`}>
          <div className="p-5 border-b border-border flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>{TABS.find((t) => t.key === tab).label}</h2>
              <p className="text-xs text-muted-foreground">{data[tab].length} entries</p>
            </div>
            <Button variant="outline" size="sm" className="rounded-full" onClick={fetchAll} disabled={loadingData} data-testid="refresh-btn">
              {loadingData ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
            </Button>
          </div>
          <div className="overflow-x-auto">
            {tab === "farmers" && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Village</TableHead>
                    <TableHead>District</TableHead>
                    <TableHead>Land</TableHead>
                    <TableHead>Crop</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.farmers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground py-10">No farmer registrations yet.</TableCell>
                    </TableRow>
                  )}
                  {data.farmers.map((r) => (
                    <TableRow key={r.id} data-testid={`farmer-row-${r.id}`}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell>{r.phone}</TableCell>
                      <TableCell>{r.village}</TableCell>
                      <TableCell>{r.district || "-"}</TableCell>
                      <TableCell>{r.land_size || "-"}</TableCell>
                      <TableCell>{r.crop_type || "-"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{fmtDate(r.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => onDelete("farmers", r.id)} className="text-destructive hover:text-destructive" data-testid={`delete-farmer-${r.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {tab === "enquiries" && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.enquiries.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground py-10">No product enquiries yet.</TableCell>
                    </TableRow>
                  )}
                  {data.enquiries.map((r) => (
                    <TableRow key={r.id} data-testid={`enquiry-row-${r.id}`}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell>{r.phone}</TableCell>
                      <TableCell>{r.email || "-"}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{r.product}</Badge>
                      </TableCell>
                      <TableCell>{r.quantity || "-"}</TableCell>
                      <TableCell className="max-w-xs truncate">{r.message || "-"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{fmtDate(r.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => onDelete("enquiries", r.id)} className="text-destructive hover:text-destructive" data-testid={`delete-enquiry-${r.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {tab === "contact" && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.contact.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-10">No contact messages yet.</TableCell>
                    </TableRow>
                  )}
                  {data.contact.map((r) => (
                    <TableRow key={r.id} data-testid={`contact-row-${r.id}`}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell>
                        <a href={`mailto:${r.email}`} className="text-primary hover:underline inline-flex items-center gap-1">
                          {r.email} <ExternalLink className="w-3 h-3" />
                        </a>
                      </TableCell>
                      <TableCell>{r.phone || "-"}</TableCell>
                      <TableCell>{r.subject || "-"}</TableCell>
                      <TableCell className="max-w-xs truncate">{r.message}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{fmtDate(r.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => onDelete("contact", r.id)} className="text-destructive hover:text-destructive" data-testid={`delete-contact-${r.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </Card>
      </main>
    </div>
  );
}
